import { bundleLineAttribute, bundleOptionMatches, bundleVariantSubtitle, calculateBundleTotals, isBundleOptionAvailable, normalizeBundleOption } from "./ekBundleMath.js";

interface BundleVariant {
  id: number;
  available: boolean;
  price: number;
  title: string;
  options: string[];
  featured_image?: {
    src?: string;
    alt?: string;
  } | null;
}

interface BundleCard {
  element: HTMLElement;
  variants: BundleVariant[];
  selects: HTMLSelectElement[];
  optionNames: string[];
  matchOptionName: string;
  discountPercent: number;
  selected: BundleVariant;
  isAnchor: boolean;
}

interface BundleVariantUpdatedDetail {
  sectionId?: string;
  variant?: BundleVariant;
  variantId?: number;
}

const optionIndexToMatch = (card: BundleCard): number => {
  const configured = normalizeBundleOption(card.matchOptionName);
  if (configured) {
    const configuredIndex = card.optionNames.findIndex((name) => normalizeBundleOption(name) === configured);
    if (configuredIndex >= 0) return configuredIndex;
  }

  return card.optionNames.findIndex((name) => {
    const normalized = normalizeBundleOption(name);
    return normalized.includes("größe") || normalized.includes("groesse") || normalized.includes("size");
  });
};

const variantFromSelections = (card: BundleCard): BundleVariant | undefined => {
  if (!card.selects.length) return card.variants.find((variant) => variant.available) ?? card.variants[0];
  const selections = card.selects.map((select) => select.value);
  return card.variants.find((variant) => variant.options.every((option, index) => option === selections[index]));
};

const bestMatchingVariant = (card: BundleCard, wantedValue: string): BundleVariant | undefined => {
  const matchIndex = optionIndexToMatch(card);
  if (matchIndex < 0) return undefined;

  const currentOptions = card.selects.map((select) => select.value);
  return card.variants
    .filter((variant) => variant.available && bundleOptionMatches(variant.options[matchIndex], wantedValue))
    .sort((left, right) => {
      const score = (variant: BundleVariant) => variant.options.reduce((total, option, index) => total + (index !== matchIndex && option === currentOptions[index] ? 1 : 0), 0);
      return score(right) - score(left);
    })[0];
};

const createBundleInstanceId = (): string => {
  const values = new Uint32Array(4);
  crypto.getRandomValues(values);
  return Array.from(values, (value) => value.toString(16).padStart(8, "0")).join("");
};

const MAX_VISIBLE_SWATCHES = 5;

class EkBuildYourBundle extends HTMLElement {
  #initialized = false;
  #cards: BundleCard[] = [];
  #button: HTMLButtonElement | null = null;
  #status: HTMLElement | null = null;
  #onVariantUpdated = (event: Event) => this.#handlePdpVariant(event as CustomEvent<BundleVariantUpdatedDetail>);

  connectedCallback() {
    if (document.readyState === "loading") return;
    this.initialize();
  }

  disconnectedCallback() {
    document.removeEventListener("productVariantUpdated", this.#onVariantUpdated);
  }

  initialize() {
    const details = this.querySelector<HTMLDetailsElement>("[data-ek-bundle-details]");
    if (details) details.open = false;

    if (this.#initialized) return;
    this.#initialized = true;

    this.#cards = Array.from(this.querySelectorAll<HTMLElement>("[data-ek-bundle-item]"))
      .map((element) => this.#createCard(element))
      .filter((card): card is BundleCard => card !== null);
    if (!this.#cards.length) return;

    if (!this.#cards.some((card) => card.isAnchor)) this.#cards[0].isAnchor = true;

    this.#button = this.querySelector<HTMLButtonElement>("[data-ek-bundle-add]");
    this.#status = this.querySelector<HTMLElement>("[data-ek-bundle-status]");

    for (const card of this.#cards) {
      for (const select of card.selects) {
        select.addEventListener("change", () => this.#handleSelection(card));
      }
      for (const swatch of Array.from(card.element.querySelectorAll<HTMLButtonElement>("[data-ek-bundle-swatch]"))) {
        swatch.addEventListener("click", () => {
          const select = card.selects.find((candidate) => candidate.id === swatch.dataset.selectId);
          if (!select || !swatch.dataset.value) return;
          select.value = swatch.dataset.value;
          select.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }
      this.#collapseSwatches(card);
      this.#renderCard(card);
    }

    this.#button?.addEventListener("click", () => void this.#addBundle());
    document.addEventListener("productVariantUpdated", this.#onVariantUpdated);
    this.#syncFromAnchor();
    this.#renderTotals();
  }

  #createCard(element: HTMLElement): BundleCard | null {
    const variantsElement = element.querySelector<HTMLScriptElement>("[data-ek-bundle-variants]");
    if (!variantsElement?.textContent) return null;

    try {
      const variants = JSON.parse(variantsElement.textContent) as BundleVariant[];
      const selects = Array.from(element.querySelectorAll<HTMLSelectElement>("[data-ek-bundle-option]"));
      const optionNames = selects.map((select) => select.dataset.optionName ?? "");
      const selected = variantFromSelections({
        element,
        variants,
        selects,
        optionNames,
        matchOptionName: element.dataset.matchOption ?? "",
        discountPercent: Number(element.dataset.discountPercent ?? 0),
        selected: variants[0],
        isAnchor: element.dataset.anchor === "true",
      });
      if (!selected) return null;

      return {
        element,
        variants,
        selects,
        optionNames,
        matchOptionName: element.dataset.matchOption ?? "",
        discountPercent: Number(element.dataset.discountPercent ?? 0),
        selected,
        isAnchor: element.dataset.anchor === "true",
      };
    } catch (error) {
      console.error("Invalid bundle variant data", error);
      return null;
    }
  }

  #handleSelection(card: BundleCard) {
    const selected = variantFromSelections(card);
    if (!selected) {
      this.#syncSwatchPressed(card);
      this.#renderAvailability(card);
      this.#setStatus(this.dataset.invalidSelection ?? "");
      this.#updateButton();
      return;
    }

    card.selected = selected;
    this.#renderCard(card);
    if (card.isAnchor) this.#syncFromAnchor();
    this.#renderTotals();
  }

  #syncFromAnchor() {
    const anchor = this.#cards.find((card) => card.isAnchor);
    if (!anchor) return;

    const anchorMatchIndex = optionIndexToMatch(anchor);
    if (anchorMatchIndex < 0) {
      this.#updateButton();
      return;
    }

    const wantedValue = anchor.selected.options[anchorMatchIndex];
    for (const card of this.#cards) {
      if (card === anchor) continue;
      const matchingVariant = bestMatchingVariant(card, wantedValue);
      if (!matchingVariant) continue;
      card.selected = matchingVariant;
      this.#applyVariantToSelects(card);
      this.#renderCard(card);
    }
    this.#updateButton();
  }

  #handlePdpVariant(event: CustomEvent<BundleVariantUpdatedDetail>) {
    if (event.detail.sectionId && event.detail.sectionId !== this.dataset.sectionId) return;
    const anchor = this.#cards.find((card) => card.isAnchor);
    if (!anchor) return;

    const variantId = Number(event.detail.variant?.id ?? event.detail.variantId);
    const selected = anchor.variants.find((variant) => variant.id === variantId);
    if (!selected) return;

    anchor.selected = selected;
    this.#applyVariantToSelects(anchor);
    this.#renderCard(anchor);
    this.#syncFromAnchor();
    this.#renderTotals();
  }

  #applyVariantToSelects(card: BundleCard) {
    for (const [index, select] of card.selects.entries()) {
      select.value = card.selected.options[index];
    }
  }

  #renderCard(card: BundleCard) {
    const image = card.element.querySelector<HTMLImageElement>(".js-ek-bundle-image");
    const variantTitle = card.element.querySelector<HTMLElement>("[data-ek-bundle-variant-title]");
    const setPrice = card.element.querySelector<HTMLElement>("[data-ek-bundle-set-price]");
    const individualPrice = card.element.querySelector<HTMLElement>("[data-ek-bundle-individual-price]");
    const discountedPrice = Math.round((card.selected.price * (100 - card.discountPercent)) / 100);

    if (image && card.selected.featured_image?.src) {
      image.removeAttribute("srcset");
      image.src = card.selected.featured_image.src;
      image.alt = card.selected.featured_image.alt ?? "";
    }
    if (variantTitle) variantTitle.textContent = bundleVariantSubtitle(card.optionNames, card.selected.options);
    if (setPrice) setPrice.textContent = this.#formatMoney(discountedPrice);
    if (individualPrice) individualPrice.textContent = this.#formatMoney(card.selected.price);
    const productUrl = card.element.dataset.productUrl;
    if (productUrl) {
      const href = `${productUrl}${productUrl.includes("?") ? "&" : "?"}variant=${card.selected.id}`;
      for (const link of Array.from(card.element.querySelectorAll<HTMLAnchorElement>("[data-ek-bundle-product-link]"))) {
        link.href = href;
      }
    }
    this.#syncSwatchPressed(card);
    this.#renderAvailability(card);
    this.#revealSelectedSwatch(card);
  }

  #syncSwatchPressed(card: BundleCard) {
    for (const swatch of Array.from(card.element.querySelectorAll<HTMLButtonElement>("[data-ek-bundle-swatch]"))) {
      const select = card.selects.find((candidate) => candidate.id === swatch.dataset.selectId);
      swatch.setAttribute("aria-pressed", String(select?.value === swatch.dataset.value));
    }
  }

  #renderAvailability(card: BundleCard) {
    const soldOutLabel = this.dataset.soldOut ?? "Ausverkauft";
    const selections = card.selects.map((select) => select.value);
    const selected = variantFromSelections(card);

    for (const select of card.selects) {
      const optionIndex = Number(select.dataset.optionIndex);
      for (const option of Array.from(select.options)) {
        const available = isBundleOptionAvailable(card.variants, selections, optionIndex, option.value);
        const label = option.dataset.label ?? option.value;
        option.textContent = available || option.selected ? label : `${label} – ${soldOutLabel}`;
      }
    }

    for (const swatch of Array.from(card.element.querySelectorAll<HTMLButtonElement>("[data-ek-bundle-swatch]"))) {
      const select = card.selects.find((candidate) => candidate.id === swatch.dataset.selectId);
      if (!select || !swatch.dataset.value) continue;
      const available = isBundleOptionAvailable(card.variants, selections, Number(select.dataset.optionIndex), swatch.dataset.value);
      swatch.classList.toggle("opacity-40", !available);
      swatch.querySelector("[data-ek-bundle-soldout-mark]")?.classList.toggle("hidden", available);
      swatch.setAttribute("aria-label", available ? swatch.dataset.value : `${swatch.dataset.value} – ${soldOutLabel}`);
    }

    this.#soldOutLabel(card).classList.toggle("hidden", Boolean(selected?.available));
    const title = card.element.querySelector<HTMLElement>("[data-ek-bundle-title]");
    const soldOut = !selected?.available;
    title?.classList.toggle("line-through", soldOut);
    title?.classList.toggle("text-secondaryhighlight", soldOut);
  }

  #soldOutLabel(card: BundleCard): HTMLElement {
    const existing = card.element.querySelector<HTMLElement>("[data-ek-bundle-sold-out]");
    if (existing) return existing;

    const label = document.createElement("p");
    label.dataset.ekBundleSoldOut = "";
    label.className = "text-right text-xs text-secondary hidden";
    label.textContent = this.dataset.soldOut ?? "Ausverkauft";
    card.element.querySelector(".mt-2.space-y-2")?.append(label);
    return label;
  }

  #collapseSwatches(card: BundleCard) {
    for (const row of Array.from(card.element.querySelectorAll<HTMLElement>("[data-ek-bundle-swatch-row]"))) {
      if (row.hasAttribute("data-ek-bundle-swatches-open")) continue;
      this.#resetSwatchRow(row);
      const swatches = Array.from(row.querySelectorAll<HTMLButtonElement>("[data-ek-bundle-swatch]"));
      if (swatches.length <= MAX_VISIBLE_SWATCHES + 1) continue;

      const selected = swatches.find((swatch) => swatch.getAttribute("aria-pressed") === "true");
      const visible = new Set<HTMLButtonElement>();
      if (selected && swatches.indexOf(selected) >= MAX_VISIBLE_SWATCHES) {
        for (const swatch of swatches) {
          if (swatch === selected) continue;
          visible.add(swatch);
          if (visible.size === MAX_VISIBLE_SWATCHES - 1) break;
        }
        visible.add(selected);
      } else {
        for (const swatch of swatches.slice(0, MAX_VISIBLE_SWATCHES)) visible.add(swatch);
      }

      const extra = swatches.filter((swatch) => !visible.has(swatch));
      for (const swatch of extra) swatch.classList.add("hidden");

      const more = document.createElement("button");
      more.type = "button";
      more.dataset.ekBundleMore = "";
      more.className = "text-xs leading-3 text-secondaryhighlight";
      more.textContent = `+${extra.length}`;
      more.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        row.dataset.ekBundleSwatchesOpen = "";
        this.#resetSwatchRow(row);
      });
      row.append(more);
    }
  }

  #resetSwatchRow(row: HTMLElement) {
    for (const swatch of Array.from(row.querySelectorAll<HTMLButtonElement>("[data-ek-bundle-swatch]"))) {
      swatch.classList.remove("hidden");
    }
    row.querySelector("[data-ek-bundle-more]")?.remove();
  }

  #revealSelectedSwatch(card: BundleCard) {
    this.#collapseSwatches(card);
  }

  #renderTotals() {
    const totals = calculateBundleTotals(
      this.#cards.map((card) => ({
        price: card.selected.price,
        discountPercent: card.discountPercent,
      })),
    );

    const setTotal = this.querySelector<HTMLElement>("[data-ek-bundle-total]");
    const individualTotal = this.querySelector<HTMLElement>("[data-ek-bundle-individual-total]");
    const individualCopy = this.querySelector<HTMLElement>("[data-ek-bundle-individual-copy]");
    const savings = this.querySelector<HTMLElement>("[data-ek-bundle-savings]");
    if (setTotal) setTotal.textContent = this.#formatMoney(totals.set);
    if (individualTotal) individualTotal.textContent = this.#formatMoney(totals.individual);
    if (individualCopy) individualCopy.textContent = this.#formatMoney(totals.individual);
    if (savings) savings.textContent = this.#formatMoney(totals.savings);
    this.#setStatus("");
    this.#updateButton();
  }

  #updateButton() {
    if (!this.#button || this.#button.getAttribute("aria-busy") === "true") return;
    this.#button.disabled = false;
  }

  #formatMoney(cents: number): string {
    const shopify = (window as any).Shopify;
    const locale = shopify?.locale && shopify?.country ? `${shopify.locale}-${shopify.country}` : "de-DE";
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: this.dataset.currency || "EUR",
    }).format(cents / 100);
  }

  #setStatus(message: string) {
    if (this.#status) this.#status.textContent = message;
  }

  async #addBundle() {
    if (!this.#button || this.#button.getAttribute("aria-busy") === "true") return;

    const selected = this.#cards.map((card) => variantFromSelections(card));
    if (selected.some((variant) => !variant)) {
      this.#setStatus(this.dataset.invalidSelection ?? "");
      return;
    }
    if (selected.some((variant) => !variant?.available)) {
      this.#setStatus(this.dataset.soldOut ?? "");
      return;
    }

    this.#button.disabled = true;
    this.#button.setAttribute("aria-busy", "true");
    this.#setStatus(this.dataset.loading ?? "");

    const includeLineKey = this.dataset.includeLineKey === "true";
    const bundleInstance = includeLineKey ? createBundleInstanceId() : "";
    try {
      const root = (window as any).Shopify?.routes?.root ?? "/";
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          items: this.#cards.map((card) => {
            const item: { id: number; quantity: number; properties?: Record<string, string> } = {
              id: card.selected.id,
              quantity: 1,
            };
            if (includeLineKey) {
              item.properties = {
                _ek_bundle: bundleLineAttribute(card.discountPercent),
                _ek_bundle_instance: bundleInstance,
              };
            }
            return item;
          }),
        }),
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { description?: string; message?: string } | null;
        throw new Error(payload?.description || payload?.message || `Cart request failed (${response.status})`);
      }

      this.#setStatus(this.dataset.added ?? "");
      window.openCartDrawerGlobal?.();
    } catch (error) {
      console.error("Unable to add bundle", error);
      this.#setStatus(this.dataset.error ?? "");
    } finally {
      this.#button.removeAttribute("aria-busy");
      this.#updateButton();
    }
  }
}

if (!customElements.get("ek-build-your-bundle")) {
  customElements.define("ek-build-your-bundle", EkBuildYourBundle);
}

let triggerInitialized = false;

export const initEkBuildYourBundle = (root: ParentNode = document) => {
  for (const element of Array.from(root.querySelectorAll<EkBuildYourBundle>("ek-build-your-bundle"))) {
    element.initialize();
  }

  if (triggerInitialized) return;
  triggerInitialized = true;
  document.addEventListener("click", (event) => {
    const trigger = (event.target as Element | null)?.closest<HTMLElement>("[data-ek-bundle-trigger]");
    if (!trigger) return;
    const details = document.getElementById(trigger.dataset.ekBundleTarget ?? "") as HTMLDetailsElement | null;
    if (!details) return;
    details.open = true;
    details.scrollIntoView({ behavior: "smooth", block: "center" });
    details.querySelector<HTMLElement>("summary")?.focus();
  });
};
